import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";

import axios, { CancelTokenSource } from "axios";

import { useStoreActions, useStoreState } from "../../store";
import { describeTourControls } from "../../services/ApiService";
import { clearTourStamps, scanControls, ScannedControl, TOUR_ATTRIBUTE } from "../../utility/controlScanner";
import {
  applyDescriptions,
  batchesFor,
  isPendingStep,
  planTour,
  scanIdOf,
} from "../../utility/dynamicTour";
import { logger } from "../../utility/logger";
import { useAuth } from "../../utility/useAuth";
import { isSupportAssistantEnabled } from "../../utility/supportAssistantAccess";
import { canReachScreen, pathForScreen, screenFor, screenForPath } from "../../utility/routes";
import { ScreenHelp, screenHelpFor } from "../../utility/screenHelp";
import {
  isSupportAssistantMounted,
  openSupportAssistant,
} from "../../utility/supportAssistantBus";
import { useAccess } from "../../access/AccessProvider";
import { subscribeWalkthrough, WalkthroughOptions } from "../../utility/walkthroughRunner";
import { Walkthrough, WalkthroughStep, walkthroughFor } from "../../utility/walkthroughs";
import ICON_X from "../../Icons/X.svg";
import {
  AskButton,
  BackButton,
  Body,
  Describing,
  FooterRight,
  Card,
  CardHeader,
  CloseButton,
  Footer,
  FooterLeft,
  Hole,
  MessageCard,
  MessageFooter,
  NextButton,
  Notice,
  Progress,
  STEP_ATTRIBUTE,
  Scrim,
  TARGET_CLASS,
} from "./WalkthroughOverlay.styles";

/**
 * THE SPOTLIGHT. One mount, at the top of the app, driven entirely by `walkthroughRunner`.
 *
 * It takes no props, and that is what makes it usable: anything in the app can start a
 * walkthrough without holding a reference to this component or being a descendant of it. The
 * only way in is `startWalkthrough(key)`.
 *
 * THE RULES IT IS BUILT AROUND
 * ----------------------------
 * 1. **The user presses the real button.** Everything this renders is `pointer-events: none`
 *    except the step card. A walkthrough that demonstrates a control the user cannot touch is
 *    a slideshow, and it goes stale against the app the moment either changes.
 * 2. **It never blocks, and it never waits on something that is not coming.** A missing anchor
 *    skips forward — straight away when the screen is plainly rendered and this control is
 *    simply not on it (`SKIP_AHEAD_MS`), on a one-second ceiling when the screen itself has
 *    not arrived yet. A screen it cannot reach is declined once, in a sentence; Escape, the
 *    Close button and any navigation the user makes themselves all end it immediately. There is
 *    no state this can get into where the user has to do something to get their screen back.
 * 3. **It never flips a mode flag silently.** A step on a screen behind the Manager view
 *    toggle turns that view on — and SAYS SO, in the card, before the step.
 * 4. **It never offers a door it knows is locked.** `canReachScreen` is asked before starting
 *    and again for any step that names a different screen. That check lives in `routes.ts` and
 *    is the same one the chips, the navigation markers and the "?" ask.
 *
 * WHY THE MEASUREMENT IS WRITTEN THE WAY IT IS
 * --------------------------------------------
 * The positioning is lifted verbatim from `components/CardActionMenu` (`getBoundingClientRect`
 * → page coordinates via `window.scrollY/scrollX` → `createPortal(document.body)`), because
 * that file already paid for the lesson: an absolutely positioned child of `body` scrolls with
 * the document, so a viewport-relative value drifts the instant anything moves. The hole needs
 * page coordinates for that reason; the card is `position: fixed` and needs viewport ones, so
 * both are stored from the same measurement rather than derived from each other later.
 */

/** How long to wait for an anchor to appear before giving up on a step. */
const ANCHOR_TIMEOUT_MS = 1000;

/**
 * One second, and it was four until 2026-09-15.
 *
 * Every page is `React.lazy` behind one `<Suspense>` boundary (`App.tsx`), so a step that
 * navigates to a screen the user has not visited this session waits on a webpack chunk over
 * the network, and the screen's own first fetch usually follows it. Four seconds was sized for
 * that: a ceiling generous enough that a cross-screen step could not fail on a first visit and
 * pass on the second, which is the worst possible shape for a bug.
 *
 * It was sized for the wrong thing. This is a ceiling nobody reaches on a screen that IS
 * rendered — the `MutationObserver` fires the moment the element lands, and a control that is
 * merely on another tab is now stepped over in a quarter of a second (`SKIP_AHEAD_MS`) rather
 * than waited for at all. What was left paying the four seconds was the user watching a dimmed
 * screen say "Looking for that part of the screen…", and four seconds of that reads as the app
 * having hung. One second is the explicit call (reported: "4 seconds is too long it needs to be
 * 1 second of looking before it skips ahead").
 *
 * ⚠️ THE TRADE IS REAL AND IT IS DELIBERATE: a screen whose chunk takes longer than a second to
 * arrive can now have its first step skipped. It degrades to a shorter tour rather than a wrong
 * one — the observer still catches the anchor for whichever step is waiting when the screen
 * paints — and the closing line already handles the case where nothing was found at all.
 */

/**
 * How long a missing anchor gets before the tour looks PAST it.
 *
 * ⚠️ THE FOUR SECONDS ABOVE ANSWER "the screen has not finished arriving". They are the wrong
 * answer to "this control is on another tab", and that is most of what a missing anchor
 * actually means (reported 2026-09-15: "the tours … say Looking for that part of the screen…
 * it just needs to go to the next one it can find"). Analytics is the worked example: the
 * builder, its tabs and the Execute/Save/Share/Export row do not exist until New report is
 * pressed (`ReportPage`: `!builderOpen ?`), and the table picker needs the Builder tab on top
 * of that — so arriving on the screen and pressing Next put SIX steps in a row on the anchor
 * clock, each one a dimmed screen saying "Looking for that part of the screen…" about a control
 * that was never going to appear.
 *
 * So the two cases are told apart by what else is on the screen: if any LATER step of this
 * walkthrough can be found right now, the screen is plainly rendered and this control is simply
 * not part of it — skip straight to that step. If nothing later can be found either, the screen
 * itself has not arrived, and that is the case `ANCHOR_TIMEOUT_MS` is for.
 *
 * The quarter-second before that decision is not a pause for the user's benefit — nothing is
 * drawn during it — it is for a control that is one commit late: a step advanced by CLICKING
 * its control (a tab, New report) resolves the next step immediately afterwards, and the panel
 * that click reveals has to be in the DOM before the lookahead reads it. React has flushed by
 * then in every case we have, but "has flushed by then" is exactly the kind of assumption that
 * fails on a slower device, and the cost of being wrong is a step silently dropped.
 */
const SKIP_AHEAD_MS = 250;

/**
 * Breathing room between the spotlit control and the edge of the hole, on every side. The hole
 * used to be the control's exact rectangle, so the dim and the ring sat hard against its edge
 * and looked like a default focus outline (2026-10-01 design review). The card keeps its 12px
 * gap from the RING, not from the control.
 */
const HOLE_PAD = 8;

interface Spot {
  /** Page coordinates, for the hole. */
  top: number;
  left: number;
  width: number;
  height: number;
  /** Viewport coordinates, for the fixed card. */
  viewTop: number;
  viewLeft: number;
}

type Phase =
  | { kind: "idle" }
  | {
      kind: "running";
      walkthrough: Walkthrough;
      index: number;
      /** Something the user did not ask for and must be told about, e.g. a mode flip. */
      notice: string | null;
      /** How many steps have found their anchor so far. Decides the closing line. */
      found: number;
    }
  | {
      kind: "message";
      text: string;

      /**
       * The one thing offered when a tour ENDS: "Ask about this screen".
       *
       * A walkthrough shows where the controls are; it cannot answer "and why is mine greyed
       * out". That question is exactly what somebody has after being shown round, and the panel
       * is the only thing in the app that answers it — so the end of a tour is where the handover
       * belongs, rather than sending them back up to the header to find the chat launcher and
       * type the question out again.
       *
       * It carries the screen's own question from `utility/screenHelp`, quoted from the corpus's
       * `### question` headings, because a near-miss on this path is expensive: the assistant no
       * longer investigates anything, so a question the guide does not cover comes back as an
       * escalation offer rather than an answer.
       *
       * Null means do not offer it — no panel is listening, or this path has no question — and a
       * button that does nothing is worse than no button (`ErrorModal`'s `help` prop, same rule).
       */
      ask: ScreenHelp | null;
    };

/**
 * The first element carrying this anchor.
 *
 * FIRST, not "the only one" — several anchors are on controls that render many times at once
 * (the card hamburger on every card, the write-back action buttons in every lane that offers
 * them). The first in document order is the top-left instance, which is the one a walkthrough
 * means. See the note in `walkthroughs.ts` on why these are attributes and not ids.
 *
 * The value is escaped before it goes into a selector. Every anchor in the registry today is a
 * plain dotted identifier, but this selector is built from a string that arrives with a screen
 * key, and a selector built by concatenation is the wrong habit to leave lying around.
 * `CSS.escape` is guarded because jsdom has shipped without it.
 */
const findAnchor = (anchor: string, attribute: string = STEP_ATTRIBUTE): HTMLElement | null => {
  const escaped =
    typeof CSS !== "undefined" && typeof CSS.escape === "function"
      ? CSS.escape(anchor)
      : anchor.replace(/["\\]/g, "\\$&");

  return document.querySelector<HTMLElement>(`[${attribute}="${escaped}"]`);
};

/**
 * The element a step points at. A SCANNED step is found by the stamp the scan left on the exact
 * element it read (`controlScanner.TOUR_ATTRIBUTE`), not by its `data-csm-step` — an anchor can
 * repeat, and the scan already chose which instance to show. A stamp that has gone (React
 * re-mounted the node) is treated exactly like a missing anchor: the step is skipped.
 */
const findStepElement = (step: WalkthroughStep): HTMLElement | null => {
  const scanId = scanIdOf(step);
  return scanId ? findAnchor(scanId, TOUR_ATTRIBUTE) : findAnchor(step.anchor);
};

/**
 * The index of the first step AFTER `index` that this screen could actually show, or -1.
 *
 * What it is for: see `SKIP_AHEAD_MS`. Finding one means the screen is rendered and the current
 * step's control is not on it, which is a thing to move past rather than to wait for.
 *
 * Two kinds of step count as reachable, and the second is the one that is easy to forget. A
 * step with no `screenKey` is here or nowhere, so its anchor decides. A step that names a
 * DIFFERENT screen has not had its chance yet — it navigates first — so its control being
 * absent from the screen we are standing on says nothing at all about it.
 *
 * `requires` is honoured because a step the user's chrome will never render is not somewhere to
 * skip TO. It would skip again immediately and land in the same place, but only after this
 * function had already reported the tour had somewhere to go — and if every remaining step is
 * behind a bit the user does not hold, that answer is simply wrong.
 */
const findSkipTarget = (
  steps: readonly WalkthroughStep[],
  index: number,
  hereKey: string | undefined,
  canRun: (step: WalkthroughStep) => boolean
): number => {
  for (let i = index + 1; i < steps.length; i += 1) {
    const step = steps[i];

    if (!canRun(step)) continue;
    if (step.screenKey && step.screenKey !== hereKey) return i;
    if (findStepElement(step)) return i;
  }

  return -1;
};

const WalkthroughOverlay: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // What the user may do, from GET api/me. Never the role bits in the store.
  const { can } = useAccess();
  const isManagerMode = useStoreState(
    (state: any) => state.appSettings?.isManagerMode ?? false
  );
  // `/home` is three screens behind one path, and WHICH of them a tour is about is decided by
  // these two flags — see the `/home` section of `walkthroughs.ts`. Read here rather than in
  // the registry because the registry is a data file and the store is not its to reach into.
  const isActiveJobMode = useStoreState(
    (state: any) => state.appSettings?.isActiveJobMode ?? true
  );
  const setIsManagerMode = useStoreActions((actions: any) => actions.appSettings.setIsManagerMode);
  const setIsActiveJobMode = useStoreActions(
    (actions: any) => actions.appSettings.setIsActiveJobMode
  );

  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [spot, setSpot] = useState<Spot | null>(null);

  /**
   * True once a step has waited long enough to be worth telling the user about.
   *
   * The card is not drawn at all before this — see `SKIP_AHEAD_MS`. A step that is about to be
   * skipped has a title and a body describing a control that is not on the screen, and painting
   * that for a frame on the way past is how a fast skip would read as a flicker of wrong text.
   * Only a step that is genuinely being WAITED for says anything, and what it says is that it
   * is looking.
   */
  const [searching, setSearching] = useState(false);

  /** The element currently spotlighted. Held in a ref so the class can come off it. */
  const targetRef = useRef<HTMLElement | null>(null);

  /**
   * The path this component navigated to itself.
   *
   * Used to tell OUR navigation from the user's. A walkthrough must end the moment the user
   * takes themselves somewhere else — they have stopped following it — but a step that moves
   * them itself must not read its own navigation as that. Same shape of problem as
   * `createSelfEchoGuard` in `utility/boardRefresh.ts`: a message you sent is a message you
   * will receive.
   */
  const selfNavigatedTo = useRef<string | null>(null);

  /** The path a running walkthrough belongs to, so a foreign navigation is detectable. */
  const runPath = useRef<string | null>(null);

  /**
   * The question the "Ask about this screen" button would send, or null to not offer it.
   *
   * Held in a ref and refreshed every render for the same reason `startRef` is: `advance` is a
   * `useCallback` whose identity is depended on by two effects, and adding the route and the
   * bus to its deps would re-create it — and re-attach the target's click listener — on every
   * navigation a walkthrough makes.
   *
   * ⚠️ IT IS READ OFF THE PATH THE USER IS ON, NOT THE KEY THE TOUR STARTED FROM. A tour can
   * move them (a step that names another screen), and the button says "this screen", so the
   * question has to be about where they ended up or the button is a lie. `screenHelpFor` is
   * the same map the "?" used to ask from, unchanged — this is now its only reader.
   *
   * `isSupportAssistantMounted` is asked here rather than at the click, because the decision
   * being made is whether to RENDER the button at all.
   */
  const askRef = useRef<ScreenHelp | null>(null);
  askRef.current = isSupportAssistantMounted()
    ? screenHelpFor(location.pathname, isManagerMode)
    : null;

  /**
   * Whether to offer "Ask about this step" on the RUNNING card — a separate question from
   * `askRef`, and deliberately not `askRef.current !== null`.
   *
   * `askRef` is null for two unrelated reasons: the panel is not mounted, or this path maps to
   * no screen in `routes.ts` and therefore has no screen-level question written for it. Only
   * the first of those has anything to do with a per-step button, whose question is built from
   * the STEP and needs no entry in `screenHelp`. Folding them together would silently drop the
   * button on any screen somebody forgot to map, which is the failure this file keeps arguing
   * against — an absent affordance leaves no trace.
   *
   * Asked in render rather than at the click for the same reason `askRef` is: the decision is
   * whether to RENDER the control. A button that does nothing when pressed is worse than no
   * button (`supportAssistantBus`), and `openSupportAssistant` returning false is the
   * race-free backstop, not the check.
   */
  const canAskRef = useRef(false);
  canAskRef.current = isSupportAssistantMounted();

  // ---------------------------------------------------------------------------------------
  // Teardown
  // ---------------------------------------------------------------------------------------

  const releaseTarget = useCallback(() => {
    targetRef.current?.classList.remove(TARGET_CLASS);
    targetRef.current = null;
  }, []);

  /**
   * The running SCANNED tour's generation, and the requests describing it.
   *
   * CoSMo's descriptions arrive seconds after the tour has started, and by then the tour may have
   * ended, or been replaced by another. The generation is bumped on every start and every end, and
   * an answer for any generation but the current one is dropped — the `CardActionMenu` stamp, same
   * hazard. The cancel source stops the requests themselves so a closed tour costs nothing more.
   */
  const tourGeneration = useRef(0);
  const describeCancel = useRef<CancelTokenSource | null>(null);

  const stopDescribing = useCallback(() => {
    tourGeneration.current += 1;
    describeCancel.current?.cancel();
    describeCancel.current = null;
    clearTourStamps();
  }, []);

  const end = useCallback(
    (message?: string) => {
      stopDescribing();
      releaseTarget();
      setSpot(null);
      setPhase(message ? { kind: "message", text: message, ask: null } : { kind: "idle" });
      runPath.current = null;
    },
    [releaseTarget, stopDescribing]
  );

  // Belt and braces: if this component is ever unmounted mid-walkthrough (a logout, a crash
  // caught upstream), the class must not be left on someone's button.
  useEffect(() => releaseTarget, [releaseTarget]);
  useEffect(() => stopDescribing, [stopDescribing]);

  const auth = useAuth();

  /**
   * Ask CoSMo for the words, in batches, and fold each answer into the running tour.
   *
   * ⚠️ NOTHING HERE CAN FAIL THE TOUR. Every step already has text; an answer improves it, a
   * failure leaves it. The only visible difference a failure makes is that the "CoSMo is
   * describing this…" line goes away, because it would otherwise promise text that is not
   * coming. The chat panel is never opened — the words go into the card.
   */
  const describe = (
    generation: number,
    screenKey: string | undefined,
    controls: ScannedControl[],
    toDescribe: Parameters<typeof describeTourControls>[2]
  ) => {
    const source = axios.CancelToken.source();
    describeCancel.current = source;

    for (const batch of batchesFor(toDescribe)) {
      const ids = batch.map((c) => c.id);

      describeTourControls(auth, screenKey, batch, source.token)
        .then((result) => {
          if (generation !== tourGeneration.current) return;

          const described = result.kind === "described" ? result.controls : [];
          if (result.kind !== "described") {
            logger.info(`WalkthroughOverlay: tour descriptions not available (${result.kind}); keeping label text.`);
          }

          setPhase((current) =>
            current.kind === "running"
              ? {
                  ...current,
                  // Every id in the batch is settled now: answered, or never going to be.
                  walkthrough: applyDescriptions(current.walkthrough, controls, screenKey, described, ids),
                }
              : current
          );
        })
        .catch(() => {
          // The only throw is the cancellation, which means the tour has already gone.
        });
    }
  };

  // ---------------------------------------------------------------------------------------
  // Starting
  // ---------------------------------------------------------------------------------------

  /**
   * Held in a ref and refreshed every render, so the subscription below can be made ONCE.
   *
   * Subscribing with `[can, ...]` in the deps would re-register on every store change, and
   * the bus is single-listener — each re-registration replaces the previous one and each
   * cleanup could clear a slot that is no longer ours. One subscription reading the latest
   * closure is the simpler half of that trade.
   */
  const startRef = useRef<(key: string, options?: WalkthroughOptions) => void>(() => {});

  startRef.current = (key: string, options?: WalkthroughOptions) => {
    if (!isSupportAssistantEnabled()) return;

    const screen = screenFor(key);

    // ---- The DYNAMIC tour: whatever is on this screen right now --------------------------
    //
    // Only for the screen the user is standing on, which is the only screen that can be read —
    // and only once `canReachScreen` agrees, the same gate as the curated path below. A scan
    // that finds nothing (a screen still loading) falls through to the curated steps rather
    // than to an empty tour.
    if (options?.scan && screen && screenForPath(location.pathname)?.key === screen.key && canReachScreen(screen, { can })) {
      stopDescribing();
      releaseTarget();

      const controls = scanControls();

      if (controls.length > 0) {
        const plan = planTour(controls, screen.key, screen.title, { isManagerMode, isActiveJobMode });
        const generation = tourGeneration.current;

        setSpot(null);
        runPath.current = location.pathname;
        setPhase({ kind: "running", walkthrough: plan.walkthrough, index: 0, notice: null, found: 0 });

        if (plan.toDescribe.length > 0) describe(generation, screen.key, controls, plan.toDescribe);
        return;
      }

      logger.info("WalkthroughOverlay: the scan found no controls; running the curated walkthrough instead.");
    }

    // The view is resolved ONCE, here, and the walkthrough it produces is held in the phase
    // state for the rest of the tour. A step that lands the user in the other view (every
    // `/home` tour ends on the tab that does) therefore finishes the tour it started rather
    // than swapping its own remaining steps out from under the person reading them.
    const walkthrough = walkthroughFor(key, { isManagerMode, isActiveJobMode });

    if (!walkthrough || !screen) {
      // A key that names nothing. The likeliest cause is a screen renamed on one side of the
      // cross-repo contract only, so it is worth a line in the console — but never an error on
      // the user's screen, because they asked for help and got an error instead.
      logger.warn(`WalkthroughOverlay: no walkthrough for key "${key}".`);
      setPhase({
        kind: "message",
        text: "I don't have a walkthrough for that screen yet.",
        ask: null,
      });
      return;
    }

    if (!canReachScreen(screen, { can })) {
      // Said ONCE, and phrased as a fact about the screen rather than about them. They may
      // simply not have that permission, which is not a mistake they made.
      setPhase({
        kind: "message",
        text: `${capitalise(screen.title)} isn't part of your access, so there's nothing to walk through there.`,
        ask: null,
      });
      return;
    }

    // A walkthrough of a PARAMETERISED screen, asked for from somewhere else.
    //
    // The step below will refuse to navigate there — the path needs a production order or a
    // sequence number this walkthrough has no way to know, and guessing one is how you show
    // somebody data they did not ask for. Left at that, the first step skips and the rest hunt
    // for `job.*` anchors on whatever screen the user is actually standing on, a second
    // each, before apologising for controls that were never going to be there. Naming the door
    // to open first is both quicker and true, and it is said HERE because this is the only place
    // that knows both where the user is and what the walkthrough wanted. The per-screen "?"
    // cannot reach this branch — it resolves its key from the path the user is already on — so
    // what this answers is a `startWalkthrough` call made from somewhere else.
    const here = screenForPath(location.pathname);
    if (screen.param && here?.key !== screen.key) {
      setPhase({
        kind: "message",
        text:
          screen.param === "order"
            ? `Open a production order first, then press ? on that screen and I'll walk you through ${screen.title}.`
            : `${capitalise(screen.title)} opens from a scanned code, so I can't take you there myself. Scan it, then press ? on that screen.`,
        ask: null,
      });
      return;
    }

    stopDescribing();
    releaseTarget();
    setSpot(null);
    runPath.current = location.pathname;
    setPhase({ kind: "running", walkthrough, index: 0, notice: null, found: 0 });
  };

  useEffect(() => subscribeWalkthrough((key, options) => startRef.current(key, options)), []);

  // ---------------------------------------------------------------------------------------
  // Advancing
  // ---------------------------------------------------------------------------------------

  const advance = useCallback(
    /**
     * `jumpTo` lands on a LATER step directly instead of on the next one.
     *
     * Only the lookahead passes it, and only with an index it has just established this screen
     * can show — every step in between has already been read and found unshowable, so stepping
     * through them one at a time would cost `SKIP_AHEAD_MS` each to reach the same place (six
     * of them on Analytics) and write six lines to the console on the way. It is a shortcut
     * through a decision already made, not a second way of deciding.
     */
    (foundThisStep: boolean, jumpTo?: number) => {
      // The two side effects happen HERE, not inside the `setPhase` updater. An updater is
      // called twice under StrictMode and may be replayed, so anything with a side effect in
      // it runs more times than it reads as running — and `releaseTarget` mutates a class on
      // somebody else's DOM node.
      releaseTarget();
      setSpot(null);

      // Resolved out here for the same reason, one step weaker: these are reads rather than
      // mutations, but they read the ROUTE and the bus, and a value captured once is what makes
      // the two closing branches below agree about what was on offer.
      const ask = askRef.current;

      setPhase((current) => {
        if (current.kind !== "running") return current;

        const found = current.found + (foundThisStep ? 1 : 0);
        const next = jumpTo ?? current.index + 1;

        if (next < current.walkthrough.steps.length) {
          return { ...current, index: next, notice: null, found };
        }

        if (found === 0) {
          // The closing line when NOTHING was found. Deliberately not "something went wrong"
          // and deliberately not "the app has changed": by far the commonest cause is a
          // permission this user does not have, so the controls the walkthrough names are
          // genuinely not on their screen. Blaming the app would send them to support for
          // something that is working as intended, and blaming them would be worse.
          return {
            kind: "message",
            text: "I couldn't find those controls on this screen. They may not be available with your permissions, or this screen may have moved on since the walkthrough was written.",

            // The branch where the handover matters MOST: the tour had nothing to show, so
            // "ask about this screen" is the only thing left that might help.
            ask,
          };
        }

        // A tour that ran. It used to end on nothing at all, which is right for the spotlight
        // and wrong for the person: being shown where the buttons are is what PRODUCES the
        // question about them, and the alternative is to dismiss the overlay, find the launcher
        // in the header and type the question out. One button, and only when there is a
        // question to ask and something listening for it — otherwise the tour still ends on
        // nothing, exactly as before.
        return ask
          ? { kind: "message", text: "That's the tour of this screen.", ask }
          : { kind: "idle" };
      });
    },
    [releaseTarget]
  );

  /**
   * Back to the nearest EARLIER step that is on screen now.
   *
   * Nearest showable, not simply `index - 1`: the step before may be one the tour skipped
   * (its control was on another tab), and landing on it would skip straight forward again to
   * where the reader already was — a Back button that does nothing. A step that names another
   * screen is not a candidate either; going back must never navigate.
   */
  const back = useCallback(() => {
    if (phase.kind !== "running") return;

    const steps = phase.walkthrough.steps;
    const hereKey = screenForPath(location.pathname)?.key;
    let target = -1;
    for (let i = phase.index - 1; i >= 0; i -= 1) {
      const elsewhere = steps[i].screenKey && steps[i].screenKey !== hereKey;
      if (!elsewhere && findStepElement(steps[i])) {
        target = i;
        break;
      }
    }
    if (target < 0) return;

    releaseTarget();
    setSpot(null);
    setPhase((current) =>
      current.kind === "running" ? { ...current, index: target, notice: null } : current
    );
  }, [phase, releaseTarget, location.pathname]);

  // ---------------------------------------------------------------------------------------
  // Resolving the current step: navigate if it names another screen, then find its anchor.
  // ---------------------------------------------------------------------------------------

  const running = phase.kind === "running" ? phase : null;
  const step: WalkthroughStep | null = running
    ? running.walkthrough.steps[running.index] ?? null
    : null;

  // Whether the access answer still lets the current step run: its own capability, and the
  // screen it names. A refresh of the answer keeps this overlay mounted in the middle of a step,
  // so the step is resolved again when this changes, and the checks below skip it. A boolean,
  // not `can`, so a refresh that keeps the grants does not resolve the step again.
  // Bumped when the control the current step is showing leaves the page (see below), which
  // resolves the step again.
  const [targetLost, setTargetLost] = useState(0);

  const stepTarget = step?.screenKey ? screenFor(step.screenKey) : null;
  const stepPermitted =
    step !== null &&
    (!step.requires || can(step.requires)) &&
    (!step.screenKey || (stepTarget !== null && canReachScreen(stepTarget, { can })));

  useEffect(() => {
    if (!running || !step) return;

    let cancelled = false;

    // Every step starts silent. Anything this one has to say about looking for its control is
    // decided below, after the screen has had `SKIP_AHEAD_MS` to prove it is rendered.
    setSearching(false);

    // ---- 0. Skip a step whose control this user's chrome does not render ----------------
    //
    // Not an access check — `canReachScreen` is that, and it has already run. This is purely
    // about not WAITING: `header.manager-tab` and `header.shopfloor-tab` are each rendered
    // behind their own capability, so for a user without it the anchor cannot arrive and the
    // anchor timeout is dead time on a step that was never going to land.
    if (step.requires && !can(step.requires)) {
      advance(false);
      return;
    }

    // ---- 1. Move to the step's screen, if it is somewhere else -------------------------
    if (step.screenKey) {
      const target = screenFor(step.screenKey);
      const here = screenForPath(location.pathname);

      if (!target || !canReachScreen(target, { can })) {
        // Per-step gate, same answer as the per-walkthrough one: refuse the step rather than
        // the whole walkthrough, so a tour that mostly applies still runs.
        logger.warn(
          `WalkthroughOverlay: step "${step.anchor}" names screen "${step.screenKey}", which this user cannot reach. Skipping.`
        );
        advance(false);
        return;
      }

      if (!here || here.key !== target.key) {
        const path = target.param ? null : pathForScreen(target);

        if (!path) {
          // A parameterised screen needs an order number this walkthrough has no way to know.
          // Skipping is the only honest option — navigating to a guessed order would be a new
          // way to show someone data they did not ask for.
          logger.warn(
            `WalkthroughOverlay: cannot navigate to "${step.screenKey}" without a parameter. Skipping step "${step.anchor}".`
          );
          advance(false);
          return;
        }

        // The Manager view toggle is a VIEW, not a permission. Some screens are only reachable
        // with it on, and the user gets told rather than finding their app rearranged.
        let notice: string | null = null;
        if (target.requiresManagerView && !isManagerMode) {
          // The pair, in this order — the `CSMShopfloorHeader.handleManagerClick` precedent.
          // Setting the first without the second lands a manager on whichever of the two
          // manager screens they last used, which is not necessarily where this step is.
          setIsManagerMode(true);
          setIsActiveJobMode(true);
          notice = "Manager view has been turned on so this screen can be shown.";
        }

        selfNavigatedTo.current = path;
        runPath.current = path;
        navigate(path);
        if (notice) setPhase((c) => (c.kind === "running" ? { ...c, notice } : c));
        // The next render runs this effect again, on the new path, and looks for the anchor.
        return;
      }
    }

    // ---- 2. Find the anchor, waiting for it if it is not there yet ----------------------
    const settle = (element: HTMLElement) => {
      if (cancelled) return;

      releaseTarget();
      targetRef.current = element;
      element.classList.add(TARGET_CLASS);

      // Scroll first, measure second. Measuring before the scroll would place the hole where
      // the control used to be and then leave it there until the next scroll event.
      // Guarded because jsdom does not implement scrollIntoView, and a walkthrough must not be
      // the reason a test environment throws.
      element.scrollIntoView?.({ block: "center", inline: "nearest" });

      // One frame for the scroll to land. `scrollIntoView` is synchronous in jsdom and
      // asynchronous in a browser, so the measurement is deferred in both.
      requestAnimationFrame(() => {
        if (!cancelled) measure(element);
      });
    };

    const immediate = findStepElement(step);
    if (immediate) {
      settle(immediate);
      return () => {
        cancelled = true;
      };
    }

    // Not there yet. It may be a lazy chunk still downloading, a grid still binding, or a
    // control behind a condition that has not become true — and those do NOT get the same
    // answer: the first two are worth waiting for and the third is worth stepping over. The
    // observer covers the first two whichever way the decision below goes.
    const observer = new MutationObserver(() => {
      const element = findStepElement(step);
      if (element) {
        observer.disconnect();
        window.clearTimeout(graceTimer);
        window.clearTimeout(timer);
        settle(element);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // ---- 2a. Is this a control that is not on the screen, or a screen that is not here? --
    const graceTimer = window.setTimeout(() => {
      if (cancelled) return;

      const aheadIndex = findSkipTarget(
        running.walkthrough.steps,
        running.index,
        screenForPath(location.pathname)?.key,
        (candidate) => !candidate.requires || can(candidate.requires)
      );

      if (aheadIndex >= 0) {
        observer.disconnect();
        window.clearTimeout(timer);

        // INFO, not warn. This is the ordinary case — a tab that is not on top, a panel that
        // opens on a button press — and the warning below is a rot detector whose value is
        // entirely in being rare. The build-time check that every anchor exists in the source
        // at all lives in `walkthroughs.test.ts`.
        logger.info(
          `WalkthroughOverlay: ${STEP_ATTRIBUTE}="${step.anchor}" is not on this screen; skipping to step ${
            aheadIndex + 1
          } ("${running.walkthrough.steps[aheadIndex].anchor}").`
        );
        advance(false, aheadIndex);
        return;
      }

      // Nothing later is on the screen either, so it is the SCREEN that has not arrived rather
      // than this control that is absent from it. That is what `ANCHOR_TIMEOUT_MS` is for, and
      // the only case in which the card says it is looking for something.
      setSearching(true);
    }, SKIP_AHEAD_MS);

    const timer = window.setTimeout(() => {
      observer.disconnect();
      if (cancelled) return;

      // A rising count of these in the field is the signal that this registry has rotted
      // against the UI in a way the build cannot see — the anchor exists in the source (that
      // much is now a test) but nothing ever renders it.
      logger.warn(
        `WalkthroughOverlay: no element carrying ${STEP_ATTRIBUTE}="${step.anchor}" after ${ANCHOR_TIMEOUT_MS}ms. Skipping this step.`
      );
      advance(false);
    }, ANCHOR_TIMEOUT_MS);

    return () => {
      cancelled = true;
      observer.disconnect();
      window.clearTimeout(graceTimer);
      window.clearTimeout(timer);
    };
    // `location.pathname` is a dependency because a navigation is how a cross-screen step
    // makes progress: the effect re-runs on the new path and looks for the anchor there.
    // `stepPermitted` is one because a refresh of the access answer can take the step away, and
    // `targetLost` because the control the step was showing can leave the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running?.walkthrough.key, running?.index, location.pathname, stepPermitted, targetLost]);

  // ---------------------------------------------------------------------------------------
  // Measuring, and keeping the measurement true
  // ---------------------------------------------------------------------------------------

  const measure = useCallback((element: HTMLElement) => {
    const rect = element.getBoundingClientRect();

    setSpot({
      // Page coordinates. The portal renders into document.body, which scrolls with the
      // document, so viewport-relative values would drift. (CardActionMenu, verbatim.)
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
      height: rect.height,
      viewTop: rect.top,
      viewLeft: rect.left,
    });
  }, []);

  useEffect(() => {
    if (!targetRef.current || !spot) return;

    let frame = 0;
    const remeasure = () => {
      // rAF-throttled: scroll fires far more often than a layout can usefully be recomputed,
      // and on a Kanban board with thousands of cards an unthrottled handler is felt.
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (targetRef.current) measure(targetRef.current);
      });
    };

    window.addEventListener("scroll", remeasure, true);
    window.addEventListener("resize", remeasure);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", remeasure, true);
      window.removeEventListener("resize", remeasure);
    };
    // `spot` is in the deps only so the listeners are attached once a target exists; they read
    // the element through the ref, so they never hold a stale one.
  }, [spot, measure]);

  // ---------------------------------------------------------------------------------------
  // Letting go of a control that leaves the page
  // ---------------------------------------------------------------------------------------

  // A refreshed access answer can take away what draws the control a step is showing, and a
  // scanned step carries no capability of its own unless a curated step names one. Whatever
  // removed it, the card would go on describing, and the spotlight go on framing, something that
  // is no longer there. So the spotlight is let go and the step is resolved again, which skips
  // to the next control the screen has, or waits for this one to come back.
  useEffect(() => {
    const element = targetRef.current;
    if (!element || !spot) return;

    const observer = new MutationObserver(() => {
      if (element.isConnected) return;
      observer.disconnect();
      releaseTarget();
      setSpot(null);
      setTargetLost((count) => count + 1);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [spot, releaseTarget]);

  // ---------------------------------------------------------------------------------------
  // Advancing on the real control being pressed
  // ---------------------------------------------------------------------------------------

  useEffect(() => {
    const element = targetRef.current;
    if (!element || !spot) return;

    const onClick = () => {
      // Deferred a tick so the control's OWN handler runs first. Advancing synchronously
      // would tear the highlight off mid-click, and on a control that opens a Syncfusion
      // popup it would race the popup's own open handler.
      window.setTimeout(() => advance(true), 0);
    };

    element.addEventListener("click", onClick);
    return () => element.removeEventListener("click", onClick);
  }, [spot, advance]);

  // ---------------------------------------------------------------------------------------
  // Escape, and the user navigating away themselves
  // ---------------------------------------------------------------------------------------

  useEffect(() => {
    if (phase.kind === "idle") return;

    const onKeyDown = (event: KeyboardEvent) => {
      // Matches every other dialog in the app. A walkthrough is something you are inside, and
      // the way out of anything you are inside here is Escape.
      if (event.key === "Escape") end();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [phase.kind, end]);

  useEffect(() => {
    if (phase.kind !== "running") return;

    if (selfNavigatedTo.current) {
      // Our own navigation, and it is seen TWICE — which is why the mark is cleared on arrival
      // rather than on sight. The step effect above is declared before this one, so on the
      // commit where a step navigates, this effect runs straight after it with the OLD pathname
      // still in hand: `runPath` already points at the destination, the router does not yet,
      // and the naive reading of that is "the user went somewhere else". It is not — it is the
      // echo of the message we just sent. The second run, after the router lands, matches and
      // clears the mark, which re-arms the check for the rest of the walkthrough.
      if (selfNavigatedTo.current === location.pathname) selfNavigatedTo.current = null;
      return;
    }

    if (runPath.current && runPath.current !== location.pathname) {
      // The user took themselves somewhere else. They have stopped following the walkthrough,
      // and a spotlight that survives onto an unrelated screen points at nothing and blocks
      // nothing, which reads as the app being stuck.
      end();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, phase.kind]);

  /**
   * The same rule for a MESSAGE card — "That's the tour of this screen.", a refusal, an apology.
   *
   * The effect above only watches a running tour, so a closing card used to ride along onto the
   * next screen and sit there talking about the one the user had left (reported 2026-09-28: "when
   * this is open and you navigate it starts there"). A message has no navigation of its own to
   * tell apart, so any change of path ends it. Keyed on the path having CHANGED, not on the phase
   * becoming a message — a tour that navigated and then finished must still show its card.
   */
  const lastPath = useRef(location.pathname);
  useEffect(() => {
    const moved = lastPath.current !== location.pathname;
    lastPath.current = location.pathname;
    if (moved && phase.kind === "message") end();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ---------------------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------------------

  if (phase.kind === "idle") return null;

  if (phase.kind === "message") {
    return createPortal(
      <MessageCard role="status" data-testid="walkthrough-message" data-csm-tour-ui="">
        <CardHeader>
          <h3>Walkthrough</h3>
          <CloseButton type="button" aria-label="Close walkthrough" onClick={() => end()}>
            <img src={ICON_X} alt="" />
          </CloseButton>
        </CardHeader>
        <Body>{phase.text}</Body>

        {/* The handover. It ENDS the overlay first: the panel opens underneath a scrim
            otherwise, and a user who has just asked to talk to the assistant cannot reach it.
            The question is the corpus's own wording, sent as a pre-fill — the panel still waits
            for Send, so a mis-click costs nothing and they can edit it into their real
            question, which is usually a sharper one than the generic screen question. */}
        {phase.ask && (
          <MessageFooter>
            <NextButton
              type="button"
              data-testid="walkthrough-ask"
              title={phase.ask.question}
              onClick={() => {
                const { question } = phase.ask!;
                end();
                openSupportAssistant({ question });
              }}
            >
              Ask CoSMo about this screen
            </NextButton>
          </MessageFooter>
        )}
      </MessageCard>,
      document.body
    );
  }

  const current = phase.walkthrough.steps[phase.index];
  if (!current) return null;

  // Below the control when it sits in the top half of the viewport, above it otherwise, so the
  // card never covers the thing it is describing. Decided from the VIEWPORT rectangle because
  // that is what "is there room on screen" means.
  const below = !spot || spot.viewTop + spot.height < window.innerHeight * 0.6;
  const cardTop = spot
    ? below
      ? spot.viewTop + spot.height + HOLE_PAD + 12
      : spot.viewTop - HOLE_PAD - 12
    : window.innerHeight - 220;
  const cardLeft = spot
    ? Math.max(12, Math.min(spot.viewLeft, window.innerWidth - 352))
    : Math.max(12, window.innerWidth / 2 - 170);

  return createPortal(
    <>
      <Scrim $dim={!spot} data-testid="walkthrough-scrim" />

      {spot && (
        <Hole
          data-testid="walkthrough-hole"
          style={{
            top: spot.top - HOLE_PAD,
            left: spot.left - HOLE_PAD,
            width: spot.width + HOLE_PAD * 2,
            height: spot.height + HOLE_PAD * 2,
          }}
        />
      )}

      {/* Nothing but the dim while a step is still resolving, unless it has been waiting long
          enough to be worth a sentence — see `searching`. The dim stays up throughout either
          way, so a skipped step reads as the tour moving on rather than as the overlay
          flickering off and back. */}
      {(spot || searching) && (
      <Card
        role="dialog"
        aria-live="polite"
        aria-label={`${phase.walkthrough.label}: ${current.title}`}
        data-testid="walkthrough-card"
        data-csm-tour-ui=""
        style={{
          top: cardTop,
          left: cardLeft,
          transform: spot && !below ? "translateY(-100%)" : undefined,
        }}
      >
        <CardHeader>
          <h3>{current.title}</h3>
          <CloseButton type="button" aria-label="Close walkthrough" onClick={() => end()}>
            <img src={ICON_X} alt="" />
          </CloseButton>
        </CardHeader>

        <Body>{spot ? current.body : "Looking for that part of the screen…"}</Body>

        {/* A scanned step whose words are still on their way. The body above is already
            true (built from the control's own label), so this is a promise of better words,
            not a wait — Next works throughout. It goes away when CoSMo answers or fails. */}
        {spot && isPendingStep(current) && (
          <Describing data-testid="walkthrough-describing">CoSMo is describing this…</Describing>
        )}

        {phase.notice && <Notice>{phase.notice}</Notice>}

        <Footer>
          <FooterLeft>
            <Progress>
              Step {phase.index + 1} of {phase.walkthrough.steps.length}
            </Progress>

            {/* The second question, asked where it actually arises.
                `screenHelp.ts` already names the gap this closes: a tour answers "where is the
                control" and cannot answer "so why is mine greyed out". That question was
                offered ONCE, by the card at the END of the tour — but it occurs AT the step,
                fifteen steps earlier, and a user who has it there either abandons the tour or
                forgets it by the time the offer arrives.

                It ENDS the tour first, exactly as the closing card's button does: the panel
                opens underneath the scrim otherwise and cannot be reached. Pre-fill, not send
                — the seed is a guess at their question built from the step's heading, so they
                must see it before it goes, and the sharper question they edit it into is the
                one worth asking. */}
            {canAskRef.current && (
              <AskButton
                type="button"
                data-testid="walkthrough-ask-step"
                title={questionForStep(current, screenForPath(location.pathname)?.title ?? null)}
                onClick={() => {
                  const question = questionForStep(
                    current,
                    screenForPath(location.pathname)?.title ?? null
                  );
                  const diagnostics = diagnosticsForStep(phase.walkthrough, phase.index, current);
                  end();
                  openSupportAssistant({ question, diagnostics });
                }}
              >
                Ask CoSMo about this step
              </AskButton>
            )}
          </FooterLeft>

          <FooterRight>
            {phase.index > 0 && (
              <BackButton type="button" data-testid="walkthrough-back" onClick={back}>
                Back
              </BackButton>
            )}
            <NextButton type="button" onClick={() => advance(Boolean(spot))}>
              {phase.index + 1 === phase.walkthrough.steps.length ? "Done" : "Next"}
            </NextButton>
          </FooterRight>
        </Footer>
      </Card>
      )}
    </>,
    document.body
  );
};

/** Screen titles in `routes.ts` are sentence fragments ("the Images screen"). */
const capitalise = (text: string): string =>
  text.length ? text.charAt(0).toUpperCase() + text.slice(1) : text;

/**
 * What "Ask about this step" pre-fills the composer with.
 *
 * ⚠️ IT IS A STARTING POINT, NOT THE QUESTION. The user's real question is very often "why is
 * MINE greyed out", which nothing here can know — so this names the thing they are looking at
 * and lets them edit the rest. The panel waits for Send precisely so they can.
 *
 * The step's HEADING is what goes in the quotes rather than its body: the heading is a control
 * label read off the component (see `walkthroughs.ts`), so it is the phrase that also appears
 * in the corpus and on the screen. The body is prose about it and would bury the noun.
 *
 * Phrased against the screen the user is ON, and the title is spliced in lower-case because
 * `routes.ts` titles are sentence fragments built to sit mid-sentence ("the Images screen") —
 * `capitalise` is for headings, not for this. A path that maps to no screen drops the clause
 * rather than the question: the step is still nameable, and half a sentence is worse than a
 * general one.
 *
 * Exported so `WalkthroughOverlay.test.tsx` can pin the wording rather than restating it.
 */
export const questionForStep = (step: WalkthroughStep, screenTitle: string | null): string =>
  screenTitle
    ? `On ${screenTitle}, what does "${step.title}" do?`
    : `What does "${step.title}" do?`;

/**
 * The locator that travels with the seed but is never shown.
 *
 * `SupportAssistantSeed.diagnostics` is "context the user did not type and should not have to",
 * and it reaches support only if the conversation ends in an escalation — it is NOT sent to the
 * model. That is the right home for this: a step is identified by a tour key, an index and an
 * anchor, none of which mean anything to the person asking, and all of which are the first
 * things somebody answering the ticket would otherwise have to ask for.
 *
 * The anchor is included because it is the one field that says whether the tour was even
 * pointing at the right control — a step whose anchor has drifted out of the DOM is the known
 * silent failure here (`walkthroughs.ts`: a missing anchor is not an error).
 */
export const diagnosticsForStep = (
  walkthrough: Walkthrough,
  index: number,
  step: WalkthroughStep
): string =>
  `Walkthrough "${walkthrough.key}", step ${index + 1} of ${walkthrough.steps.length}: ` +
  `"${step.title}" (anchor ${step.anchor})`;

export default WalkthroughOverlay;
